import React, { FC, useEffect, useState } from "react";
import {
  ActionTypeEnum,
  HasFormIdModel,
  RequestActionModel,
  SystemModulesEnum,
  UserRegistrationResponseModel,
} from "../../models";
import {
  isArabicCurrentLanguage,
  isSystemConfigurationModuleEnabled,
} from "../../utils";
import { GridTable, LoadingBox, PrivilegesChecker, SearchBox } from "..";
import { getUsers } from "../../serviceBroker/userApiServiceBroker";
import { useSearchBox, useSystemConfigurations } from "../../hooks";

interface UsersListProps extends HasFormIdModel {
  onActionEvent?: (o: RequestActionModel) => void;
  isRefresh: boolean;
}

export const UsersList: FC<UsersListProps> = ({
  onActionEvent = () => {},
  formId,
  isRefresh = false,
}) => {
  //#region variables
  const isArabic: boolean = isArabicCurrentLanguage();
  const defaultPageSize: number = 50;
  //#endregion
  //#region state
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserRegistrationResponseModel[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<
    UserRegistrationResponseModel[]
  >([]);
  const { onChange, onSearch, searchedWord, value } = useSearchBox();
  const { userConfiguration, isTransactionLoaded } =
    useSystemConfigurations(setLoading);
  //#endregion
  //#region functions
  const getAllUsers = async () => {
    setLoading(true);
    const userList = await getUsers();
    setUsers(userList);
    setFilteredUsers(userList);
    setLoading(false);
  };
  const availableActions = [
    ActionTypeEnum.GrantPermissions,
    ActionTypeEnum.UserSetting,
    ActionTypeEnum.UserStorePermission,
    ActionTypeEnum.UserBranchPermission,
    ActionTypeEnum.TransactionSetting,
    isSystemConfigurationModuleEnabled(
      userConfiguration.systemModulesConfigurations,
      (p) => p.ID === SystemModulesEnum.POS
    ) && ActionTypeEnum.POSSetting,
    ActionTypeEnum.Update,
    ActionTypeEnum.Delete,
  ].filter((action): action is ActionTypeEnum => Boolean(action));
  //#endregion
  //#region useEffect
  useEffect(() => {
    const fillData = async () => {
      await getAllUsers();
    };
    fillData().then(() => {});
  }, []);
  useEffect(() => {
    if (isRefresh) {
      const fillData = async () => {
        await getAllUsers();
      };
      fillData().then(() => {});
      onActionEvent({ action: ActionTypeEnum.Refresh, request: false });
    }
  }, [isRefresh]);
  useEffect(() => {
    const searchValue =
      searchedWord && searchedWord.trim() !== "" ? searchedWord : value;
    if (!searchValue.trim()) {
      setFilteredUsers(users);
    } else {
      // Filter based on Arabic or English name
      setFilteredUsers(
        users.filter(
          (p) =>
            p.User_Name.toLowerCase()
              .trim()
              .includes(searchValue.toLowerCase().trim()) || // Match Username
            p.Name.toLowerCase()
              .trim()
              .includes(searchValue.toLowerCase().trim()) || // Match Arabic name
            p.Name_En.toLowerCase()
              .trim()
              .includes(searchValue.toLowerCase().trim()) // Match English name
        )
      );
    }
  }, [searchedWord, value]);
  //#endregion
  //#region html
  return (
    <>
      {loading || (!isTransactionLoaded && <LoadingBox />)}
      <PrivilegesChecker formId={formId} action="EnableSearch">
        <SearchBox
          onChangeHandler={onChange}
          onSearchHandler={onSearch}
          searchedWord={value}
          name="name"
        />
      </PrivilegesChecker>
      <GridTable
        columnsProps={{
          actions: availableActions,
          isArabic,
          onActionEvent: onActionEvent,
        }}
        formId={formId}
        data={filteredUsers}
        totalRows={filteredUsers.length}
        currentPage={1}
        paginationType="client"
        pageSize={defaultPageSize}
        // style={TableActionColumnsStyleEnum.DropDownText}
        // columns={generateColumnList()}
      />
    </>
  );
  //#endregion
};
